import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Crown, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Common";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | slow | error
  const [eventId, setEventId] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const check = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.event_id) setEventId(data.event_id);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      if (tries.current >= 8) { setState("slow"); return; }
      timer = setTimeout(check, 2000);
    };
    check();
    return () => clearTimeout(timer);
  }, []);

  const goManage = () => navigate(eventId ? `/events/${eventId}` : "/dashboard");

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="w-full max-w-md rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 text-center" data-testid="payment-success-page">
        <div className="flex justify-center mb-6"><Logo /></div>
        {state === "checking" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-[hsl(var(--accent))]" />
            <h1 className="mt-5 font-display text-2xl font-semibold">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="h-16 w-16 mx-auto grid place-items-center rounded-full bg-[hsl(35_55%_90%)] text-[hsl(var(--accent))]"><CheckCircle2 size={34} /></div>
            <h1 className="mt-5 font-display text-2xl font-semibold" data-testid="payment-success-title">You're all set</h1>
            <p className="mt-2 text-[hsl(var(--muted-foreground))]">Your tribute has been upgraded to <strong>Keepsake</strong> — no watermark, stored forever, and unlimited tributes.</p>
            <button data-testid="payment-success-continue" onClick={goManage}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity">
              <Crown size={16} /> Back to your tribute <ArrowRight size={16} />
            </button>
          </>
        )}
        {state === "slow" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-[hsl(var(--accent))]" />
            <h1 className="mt-5 font-display text-2xl font-semibold">Almost there</h1>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Your payment is processing. Your upgrade will appear shortly — you can head back and refresh.</p>
            <button onClick={goManage} data-testid="payment-slow-continue" className="mt-6 w-full px-6 py-3.5 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))]">Go to my tribute</button>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="mt-2 font-display text-2xl font-semibold">Something went wrong</h1>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">We couldn't confirm this payment. If you were charged, it will still be applied automatically.</p>
            <Link to="/dashboard" data-testid="payment-error-home" className="mt-6 inline-block font-semibold underline">Back to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
